/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { SimpleAttributeList, SimpleAttribute, wildCardAttribute } from './SimpleAttribute';
import deepcopy from 'deepcopy';

/**
 * More boilerplate reduction, SplitData inheritors can use this if they only require the default database/ edit view combination.
 * To use:
 *
 * ```
 * class Plan extends SplitData<PlanFields, DefaultSplitDataRenderType>
 * ```
 *
 * To override:
 * ```
 * export enum PlanRenderType {
 *   database = 'database',
 *   edit = 'edit',
 *   somethingDifferent = 'something-different'
 * }
 * class Plan extends SplitData<PlanFields, PlanRenderType>
 * ```
 */
export enum DefaultSplitDataRenderType {
	database = 'database',
	edit = 'edit',
}

/**
 * # Brief
 * A generic data model that is meant to have multiple duplicate "views" (database and edit are the most comment) of the same data
 * with a means to toggle the selected view and access said selected view via the `view` property.
 * This is done to facillitate the common workflow of always being able to have fallback data when running a longer edit operation
 * where that fallback data also remains current with the active value in firebase.
 * The vast majority of "end user" code using a SplitData extended object should only use `view`
 *
 * # Usage flow:
 * - implement the views you wish to use in the child class, along with the enum and view interface
 * - initialize by constructing, setting the attribute list, than calling databaseUpdate
 * (along with subscribing for more database updates in whatever way)
 * - when changing the local data call localUpdate with the changed data (undefined keys are ignored, nulls mean remove)
 * - when working with something involving many local changes you'll want to change views at the beginning
 * of the operation, use localUpdate during, then change back at the end
 * - after this, use generateDatabaseOuput to create a firebase writing object representing the new data
 *
 * When adding custom behavior override and (usually) super the public methods
 */
export abstract class SplitData<Fields extends {}, RenderType extends string> {
	public view: Fields = {} as Fields;
	public $id: string;
	/**
	 * Use this in the ui as part of selecting an item(s) and then loading additional data to indicate that loading is done.
	 * Override as false in your subclass as part of enabling this. Defaults to true since data is _usually_ preloaded.
	 */
	public asyncSelectionLoaded: boolean = true;

	//in the future, these can made pubic when/ if necessary
	//basically everything wants a database/ edit view, so just build these in
	protected database: Fields = {} as Fields;
	protected edit: Fields = {} as Fields;

	public primaryType: RenderType; //can probably do better here
	public currentRenderType: RenderType;
	/*
	attribute list used for:
		- defines rules keyed off of attribute keys specifying rules for each key
		- reading data from firebase
		- reading data from local sources
		- how to move data when switching between views
		- what data to include when outputting data
	*/
	protected abstract attributeList: SimpleAttributeList;

	//temp thing to avoid dealing with _everything else_ right now
	protected useInPlace: boolean = false;

	// this flag used to indicate local data added by addItem() that haven't been persisted yet
	public localDataNotYetPersisted: boolean = false;

	public constructor($id: string, primaryType: RenderType) {
		this.primaryType = this.currentRenderType = primaryType;
		this.view = this[primaryType as keyof SplitData<Fields, RenderType>] as Fields;
		this.$id = $id;
	}

	/**
	 * An update specifically intended to operate on the primaryType, will apply the database -> local data transformations.
	 * Might be better to simply have a flag for applying database transformations
	 * @param  data The object containing the data.
	 */
	public databaseUpdate(data: object): void {
		//TODO - refactor old stuff to use this one and verify "no break"
		if (this.useInPlace) {
			return this.inPlaceDatabaseUpdate(data);
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(this as any)[this.primaryType] = data; //maybe switch to (this as {[primaryType]: Fields})[this.primaryType] = data;

		//reset this because primary "probably" is a new object each time. Consider creating a "rawPrimary"
		// to track that instead and maintain a "same object" thing for primary.
		// Could also do that without the raw one, depends on need.
		if (this.currentRenderType === this.primaryType) {
			this.view = this[this.primaryType as keyof SplitData<Fields, RenderType>] as Fields;
		}
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	private getAttribute(key: string): SimpleAttribute {
		if (this.attributeList && this.attributeList[key]) {
			return this.attributeList[key];
		} else if (this.attributeList && this.attributeList[wildCardAttribute]) {
			return this.attributeList[wildCardAttribute];
		} else {
			return { read: false, write: false, copy: false };
		} //dead attribute, may need to be null?
	}

	/** applies all of the read configured data from the database to the primary view */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	private inPlaceDatabaseUpdate(data: object): void {
		const dbView = this[this.primaryType as keyof SplitData<Fields, RenderType>] as Fields;

		//remove all keys not included in the db update
		// eslint-disable-next-line guard-for-in
		for (const key in dbView) {
			const attr = this.getAttribute(key);
			if (attr.read) {
				delete dbView[key];
			}
		}

		//update all keys that are
		// eslint-disable-next-line guard-for-in
		for (const key in data) {
			const attr = this.getAttribute(key);
			if (attr.read) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				dbView[key as keyof Fields] = (data as any)[key];
			}
		}
	}

	/**
	 * Apply local udpates (to the current view generally)
	 * @param  data       Data that should be changed, set to null to remove an attribute
	 * @param  currentRenderType Override the render type, defaults to current
	 */
	public localUpdate(data: object, renderType?: RenderType): void {
		renderType = this.defType(renderType);
		const view = this[renderType as keyof SplitData<Fields, RenderType>] as Fields;

		// eslint-disable-next-line guard-for-in
		for (const key in data) {
			//not currently necessary to validate against the attribute config, this is always allowed
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			view[key as keyof Fields] = (data as any)[key];
		}
	}

	/**
	 * Copys data from the current renderType to a new renderType
	 * @param  renderType   The renderType to change to
	 * @param  cancel=false If true, don't copy data and just change the type. This effectively throws out any changes made in that view
	 * @param targetRenderOverride	In case you want to copy the data from a different view for whatever reason
	 */
	public switchViews(
		renderType: RenderType,
		cancel: boolean = false,
		targetRenderOverride?: RenderType
	): void {
		if (!targetRenderOverride) {
			targetRenderOverride = this.currentRenderType;
		}
		if (renderType === targetRenderOverride) {
			return;
		}
		if (!cancel) {
			this.copyDataBetweenViews(targetRenderOverride, renderType);
		}
		this.currentRenderType = renderType;
		this.view = this[renderType as keyof SplitData<Fields, RenderType>] as Fields;
	}

	/**
	 * Standardized way to indicate that a SplitData inheritor has finished loaded supplemental data after being selected.
	 * Should only be called once in the life of the model and is entirely opt in.
	 * @param loaded Defaults to true, but false
	 */
	public setSelectionLoaded(loaded: boolean = true): void {
		this.asyncSelectionLoaded = loaded;
	}

	/**
	 * Convert a view into database data to be returned and written. Will only clear keys if they were previously set to null by localUpdate
	 * @param  renderType Type, defaults to the database instead of the current type
	 * @return            An object that is the database representation of the current state of the view. Use to persist changes.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public generateDatabaseOutput(renderType?: RenderType): { [key: string]: any } {
		if (!renderType) {
			renderType = this.primaryType;
		}
		const view = this[renderType as keyof SplitData<Fields, RenderType>] as Fields;
		const writeObj = {};

		// eslint-disable-next-line guard-for-in
		for (const key in view) {
			const attr = this.getAttribute(key);
			if (view[key] !== undefined && attr.write) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(writeObj as any)[key] = this.copyProvider(view[key], key);
			}
		}

		return writeObj;
	}

	/**
	 * A hook to override the method of copying an attribute between views
	 * deepcopy already internally does fairly clean type checks and does the shallow vs non-shallow copy thing.
	 * So no reason to reinvent the wheel for the default implementation
	 * The key property is unused for the default implementation, but will be necessary if overriden
	 */
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
	protected copyProvider(item: any, key: string) {
		return deepcopy(item);
	}

	/** does the actual work of copying data between views */
	protected copyDataBetweenViews(srcRenderType: RenderType, targetRenderType: RenderType): void {
		const srcView = this[srcRenderType as keyof SplitData<Fields, RenderType>] as Fields;
		const targetView = this[targetRenderType as keyof SplitData<Fields, RenderType>] as Fields;

		//empty the target if it's not the primary view
		if (targetRenderType !== this.primaryType) {
			// eslint-disable-next-line guard-for-in
			for (const key in targetView) {
				delete targetView[key];
			}
		}

		// eslint-disable-next-line guard-for-in
		for (const key in srcView) {
			const attr = this.getAttribute(key);
			if (attr.copy) {
				targetView[key] = this.copyProvider(srcView[key], key);
			}
		}
	}

	/** slightly abstract the "override render or use default" thing */
	protected defType(renderType?: RenderType): RenderType {
		return renderType || this.currentRenderType;
	}
}
